// ============================================================
// Datei: GuestListDesktop.jsx
//
// ÄNDERUNGEN:
// - FIX: Filter basiert jetzt auf recordedById (stabil)
// - Fallback auf Name falls ID fehlt
// - Kein Bug mehr → Liste bleibt sichtbar
// - NEU: Default-Sortierung = guestId desc
//        => zuletzt hinzugefügter Gast steht beim Laden zuoberst
// - bestehende Sortierung über Tabellenköpfe bleibt erhalten
// ============================================================

import React, { useMemo, useState } from "react";
import "./guestList.css";

const GuestListDesktop = ({
    guests = [],
    onEdit = null,
    onDelete = null
}) => {

    // ============================================================
    // USER INFOS AUS LOCAL STORAGE
    // ============================================================
    const role = localStorage.getItem("role");
    const department = localStorage.getItem("department");
    const volunteerId = localStorage.getItem("volunteerId");
    const username = localStorage.getItem("username"); // fallback

    // ============================================================
    // FILTER LOGIK (STABIL)
    // ============================================================
    const filteredGuests = useMemo(() => {

        // ADMIN sieht alles
        if (role === "ADMIN") return guests;

        // OK von KASSE sieht alles
        if (role === "OK" && department?.toLowerCase() === "kasse") {
            return guests;
        }

        // OK → nur eigene Gäste
        if (role === "OK") {
            return guests.filter(g => {

                // BEST CASE → ID vergleichen
                if (g.recordedById && volunteerId) {
                    return String(g.recordedById) === String(volunteerId);
                }

                // FALLBACK → Name vergleichen
                return g.recordedByFullName
                    ?.toLowerCase()
                    .includes(username?.toLowerCase());
            });
        }

        // fallback
        return [];

    }, [guests, role, department, volunteerId, username]);

    // ============================================================
    // SORTIER-STATE
    //
    // WICHTIG:
    // Default ist jetzt guestId DESC.
    // Dadurch wird beim ersten Laden immer der neueste Gast
    // ganz oben angezeigt.
    // ============================================================
    const [sortConfig, setSortConfig] = useState({
        key: "guestId",
        direction: "desc"
    });

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc"
                };
            }

            // Bei neu gewählter Spalte startet die Sortierung wie bisher mit asc
            return {
                key,
                direction: "asc"
            };
        });
    };

    // ============================================================
    // SORTIERUNG
    //
    // Default:
    // - guestId DESC = neuester Gast oben
    //
    // Danach:
    // - alle anderen Attribute können wie bisher
    //   über die Tabellenköpfe sortiert werden
    // ============================================================
    const sortedGuests = useMemo(() => {
        if (!sortConfig.key) return filteredGuests;

        return [...filteredGuests].sort((a, b) => {
            const aVal = a?.[sortConfig.key];
            const bVal = b?.[sortConfig.key];

            // --------------------------------------------------------
            // null / undefined defensiv behandeln
            // --------------------------------------------------------
            if (aVal == null && bVal == null) return 0;
            if (aVal == null) return sortConfig.direction === "asc" ? -1 : 1;
            if (bVal == null) return sortConfig.direction === "asc" ? 1 : -1;

            // --------------------------------------------------------
            // Wenn beide Werte numerisch sind:
            // ideal für guestId
            // --------------------------------------------------------
            const aNum = Number(aVal);
            const bNum = Number(bVal);
            const bothNumeric = !Number.isNaN(aNum) && !Number.isNaN(bNum);

            if (bothNumeric) {
                return sortConfig.direction === "asc"
                    ? aNum - bNum
                    : bNum - aNum;
            }

            // --------------------------------------------------------
            // Sonst als String vergleichen
            // --------------------------------------------------------
            const aStr = String(aVal).toLowerCase();
            const bStr = String(bVal).toLowerCase();

            return sortConfig.direction === "asc"
                ? aStr.localeCompare(bStr, "de", { sensitivity: "base" })
                : bStr.localeCompare(aStr, "de", { sensitivity: "base" });
        });
    }, [filteredGuests, sortConfig]);

    const sortArrow = (key) =>
        sortConfig.key === key
            ? sortConfig.direction === "asc"
                ? " ▲"
                : " ▼"
            : "";

    // ============================================================
    // RENDER
    // ============================================================
    return (
        <div className="guest-list-container">
            <h3 className="guest-list-title">Gästeliste</h3>

            {sortedGuests.length === 0 ? (
                <p className="guest-list-empty">Schreibe deine Gäste ein. Du kannst 2 Eintritte verschenken. (Festivalpass = 2 Eintritte)</p>
            ) : (
                <table className="guest-list-table">
                    <thead>
                        <tr>
                            <th onClick={() => handleSort("firstName")}>
                                Vorname{sortArrow("firstName")}
                            </th>
                            <th onClick={() => handleSort("lastName")}>
                                Nachname{sortArrow("lastName")}
                            </th>
                            <th onClick={() => handleSort("admission")}>
                                Eintritt{sortArrow("admission")}
                            </th>
                            <th onClick={() => handleSort("roleName")}>
                                Rolle{sortArrow("roleName")}
                            </th>
                            <th onClick={() => handleSort("recordedByFullName")}>
                                Erfasst von{sortArrow("recordedByFullName")}
                            </th>
                            <th>Aktionen</th>
                        </tr>
                    </thead>

                    <tbody>
                        {sortedGuests.map(g => (
                            <tr key={g.guestId}>
                                <td>{g.firstName}</td>
                                <td>{g.lastName}</td>
                                <td>{g.admission}</td>
                                <td>{g.roleName}</td>
                                <td>{g.recordedByFullName}</td>

                                <td className="guest-actions">
                                    <button
                                        className="btn-edit"
                                        disabled={!onEdit}
                                        onClick={() => onEdit && onEdit(g)}
                                    >
                                        ✏️
                                    </button>

                                    <button
                                        className="btn-delete"
                                        disabled={!onDelete}
                                        onClick={() => onDelete && onDelete(g)}
                                    >
                                        🗑️
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default GuestListDesktop;